import { type SchemaMetaOrderDeliveryChunk } from '@/shared/api/openApi';
import { type DeliveryChunk, type OrderDto, type OrderPersistenceDto } from '@/shared/api/order';
import { OrderDeliveryMethods } from '@/shared/api/order';
import { type ArrayIntervalFieldValue, type IntervalFieldValue } from '@/shared/components/IntervalInput';
import { toISOString } from '@/shared/lib/datetime';
import { getDefaultPaymentMethod } from '@/shared/lib/getDefaultPaymentMethod';
import type { OrderFormValues } from '../../model/forms/orderForm';
import { checkCourierAllPaymentMethods } from '../checkCourierAllPaymentMethods';

const getPaymentMethod = (
    courierIntervals: ArrayIntervalFieldValue,
    values: OrderFormValues,
    deliveryChunks: Array<DeliveryChunk>
) =>
    checkCourierAllPaymentMethods(courierIntervals, deliveryChunks) && values.paymentMethod
        ? values.paymentMethod
        : getDefaultPaymentMethod();

interface ChunkIntervalFieldValue {
    intervalField: IntervalFieldValue;
    chunkId: number;
}

const toOrderPersistenceDtoCourierChunk = (chunk: ChunkIntervalFieldValue) => {
    return {
        id: chunk.chunkId,
        courier_delivery_service_type:
            chunk.intervalField.type === 'interval' ? chunk.intervalField.serviceType : undefined,
        courier_interval:
            chunk.intervalField.type === 'interval'
                ? {
                      from: toISOString(chunk.intervalField.from),
                      to: toISOString(chunk.intervalField.to),
                  }
                : {},
    };
};

const toOrderPersistenceDtoPickupChunk = (chunk: ChunkIntervalFieldValue) => {
    return {
        id: chunk.chunkId,
        pickup_point_interval:
            chunk.intervalField.type === 'interval'
                ? {
                      from: toISOString(chunk.intervalField.from),
                      to: toISOString(chunk.intervalField.to),
                  }
                : {},
    };
};

const toOrderPersistenceDtoSelfPickupChunk = (chunk: ChunkIntervalFieldValue) => {
    return {
        id: chunk.chunkId,
    };
};

const getDeliveryChunks = (
    chunks: Array<DeliveryChunk>,
    intervals: Array<ChunkIntervalFieldValue>,
    transformFunction: (interval: ChunkIntervalFieldValue) => SchemaMetaOrderDeliveryChunk
) => {
    const chunkIntervals: Array<ChunkIntervalFieldValue | undefined> = chunks.map((chunk) =>
        intervals.find((interval) => interval.chunkId === chunk.id)
    );

    return chunkIntervals.filter((interval) => interval !== undefined).map(transformFunction);
};

export function fromFormValues(
    orderId: string,
    values: OrderFormValues,
    order: OrderDto
): OrderPersistenceDto | undefined {
    const client: OrderPersistenceDto['client'] = {
        first_name: values.client.firstName,
        last_name: values.client.lastName,
        phone: values.client.phone,
        email: values.client.email,
    };

    if (values.deliveryMethod === OrderDeliveryMethods.COURIER) {
        const courierIntervals = values.deliveryCourierInterval;

        return {
            order_id: orderId,
            client: client,
            payment_method: getPaymentMethod(courierIntervals, values, order.delivery_chunks),
            user_agreed_to_charge_bonuses: values.bonusesApplied,
            address: {
                ...values.addressCourier,
                courier_address: values.addressCourier.address,
                courier_locality: values.addressCourier.locality || values.locality,
                address: '',
                locality: '',
            },
            delivery_notes: values.addressCourier.comment,
            delivery_method: OrderDeliveryMethods.COURIER,
            delivery_chunks: getDeliveryChunks(
                order.delivery_chunks,
                values.deliveryCourierInterval,
                toOrderPersistenceDtoCourierChunk
            ),
        };
    }

    if (values.deliveryMethod === OrderDeliveryMethods.PICKUP_POINT) {
        return {
            order_id: orderId,
            client: client,
            payment_method: values.paymentMethod || getDefaultPaymentMethod(),
            user_agreed_to_charge_bonuses: values.bonusesApplied,
            address: {
                pickup_point_address: values.addressPickup.address,
                pickup_point_locality: values.addressPickup.locality || values.locality,
                pickup_point_id: values.addressPickup.pickupPointId ? values.addressPickup.pickupPointId : undefined,
            },
            delivery_notes: '',
            pickup_point_delivery_service_type: values.addressPickup.serviceType,
            delivery_method: OrderDeliveryMethods.PICKUP_POINT,
            delivery_chunks: getDeliveryChunks(
                order.delivery_chunks,
                values.deliveryPickupPointInterval,
                toOrderPersistenceDtoPickupChunk
            ),
        };
    }

    if (values.deliveryMethod === OrderDeliveryMethods.SELF_PICK_UP) {
        return {
            order_id: orderId,
            client: client,
            payment_method: values.paymentMethod || getDefaultPaymentMethod(),
            delivery_method: OrderDeliveryMethods.SELF_PICK_UP,
            delivery_notes: '',
            address: {
                self_pick_up_address: values.addressSelfPickUp.address || undefined,
                self_pick_up_locality: values.addressSelfPickUp.locality || values.locality || undefined,
                self_pick_up_warehouse_id: values.addressSelfPickUp.pickUpWarehouseId || undefined,
            },
            user_agreed_to_charge_bonuses: values.bonusesApplied,
            delivery_chunks: getDeliveryChunks(
                order.delivery_chunks,
                values.deliverySelfPickUpInterval,
                toOrderPersistenceDtoSelfPickupChunk
            ),
        };
    }

    return undefined;
}
